import {
  BarChart3,
  Box,
  BrickWall,
  Database,
  Hexagon,
  History,
  LayoutGrid,
  Leaf,
  Terminal,
  Link2,
  Lock,
  Package,
  Plug,
  Server,
  Settings,
  Shield,
  Snowflake,
  UserRound,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { DataSourceKind, ModelTabId, SpaceIconKey, SpaceTabId, TableDetailTabId } from './dataModelsTypes';

/** Entities catalog id. Data Models used this until it received its own grant. */
export const ENTITIES_PAGE_ID = 'f6g7h8i9-j0k1-2345-f123-456789012345';
export const DATA_MODELS_PAGE_ID = '8f2a1c3e-6b47-4d9a-91e0-2c5d7a8b4e16';
export const DATA_INGESTION_PAGE_ID = '3b9e7d14-5c28-4a1f-8d06-9e4f2b7c1a53';
export const DATA_MODELS_PATH = '/portal/data-models';
export const DATA_INGESTION_PATH = '/portal/configurations/data-ingestion';

export function isLegacyInheritingDataPageId(id: string): boolean {
  return id === DATA_MODELS_PAGE_ID || id === DATA_INGESTION_PAGE_ID;
}

export const SPACE_TABS: { id: SpaceTabId; label: string; icon: LucideIcon }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'models', label: 'Data Models', icon: Box },
  { id: 'connections', label: 'Connections', icon: Link2 },
  { id: 'governance', label: 'Governance', icon: Shield },
  { id: 'mcp', label: 'MCP', icon: Plug },
  { id: 'assets', label: 'Assets', icon: Database },
  { id: 'activity', label: 'Activity', icon: History },
  { id: 'queries', label: 'Logs', icon: Terminal },
];

export const HIDDEN_TABLE_DETAIL_TAB_IDS = new Set<TableDetailTabId>([
  'relationships',
  'lineage',
  'docs',
]);

export const TABLE_DETAIL_TABS: { id: TableDetailTabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'schema', label: 'Schema' },
  { id: 'preview', label: 'Data Preview' },
  { id: 'governance', label: 'Governance' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'usage', label: 'Usage' },
  { id: 'lineage', label: 'Lineage' },
  { id: 'docs', label: 'Documentation' },
  { id: 'activity', label: 'Activity' },
  { id: 'queries', label: 'Logs' },
];

export const MODEL_TABS: { id: ModelTabId; label: string }[] = [
  { id: 'tables', label: 'Assets' },
  { id: 'sources', label: 'Data Sources' },
  { id: 'mcp', label: 'MCP Tools' },
  { id: 'lineage', label: 'Schema & Lineage' },
  { id: 'governance', label: 'Access & Governance' },
];

export const SPACE_ICON: Record<SpaceIconKey, LucideIcon> = {
  customer: Users,
  sales: BarChart3,
  finance: Wallet,
  operations: Settings,
  hr: UserRound,
  product: Package,
  risk: Shield,
  it: Lock,
};

export const SPACE_TONE: Record<SpaceIconKey, { tile: string; dark: string }> = {
  customer: { tile: 'bg-sky-100 text-sky-700', dark: 'bg-sky-500/20 text-sky-200' },
  sales: { tile: 'bg-emerald-100 text-emerald-700', dark: 'bg-emerald-500/20 text-emerald-200' },
  finance: { tile: 'bg-amber-100 text-amber-700', dark: 'bg-amber-500/20 text-amber-200' },
  operations: { tile: 'bg-violet-100 text-violet-700', dark: 'bg-violet-500/20 text-violet-200' },
  hr: { tile: 'bg-teal-100 text-teal-700', dark: 'bg-teal-500/20 text-teal-200' },
  product: { tile: 'bg-orange-100 text-orange-700', dark: 'bg-orange-500/20 text-orange-200' },
  risk: { tile: 'bg-rose-100 text-rose-700', dark: 'bg-rose-500/20 text-rose-200' },
  it: { tile: 'bg-indigo-100 text-indigo-700', dark: 'bg-indigo-500/20 text-indigo-200' },
};

export const SOURCE_META: Record<DataSourceKind, { label: string; icon: LucideIcon; tile: string; dark: string }> = {
  snowflake: { label: 'Snowflake', icon: Snowflake, tile: 'bg-cyan-100 text-cyan-700', dark: 'bg-cyan-500/20 text-cyan-200' },
  postgresql: { label: 'PostgreSQL', icon: Database, tile: 'bg-indigo-100 text-indigo-700', dark: 'bg-indigo-500/20 text-indigo-200' },
  mongodb: { label: 'MongoDB', icon: Leaf, tile: 'bg-emerald-100 text-emerald-700', dark: 'bg-emerald-500/20 text-emerald-200' },
  bigquery: { label: 'BigQuery', icon: Hexagon, tile: 'bg-blue-100 text-blue-700', dark: 'bg-blue-500/20 text-blue-200' },
  sqlserver: { label: 'SQL Server', icon: Server, tile: 'bg-red-100 text-red-700', dark: 'bg-red-500/20 text-red-200' },
  databricks: { label: 'Databricks', icon: BrickWall, tile: 'bg-orange-100 text-orange-700', dark: 'bg-orange-500/20 text-orange-200' },
};

export const MODEL_TILE = {
  light: 'bg-violet-100 text-violet-700',
  dark: 'bg-violet-500/20 text-violet-200',
} as const;

export const TABLE_TILE = {
  light: 'bg-indigo-100 text-indigo-700',
  dark: 'bg-indigo-500/20 text-indigo-200',
} as const;

export const MODEL_ICON = Box;

export const SOURCE_KIND_OPTIONS = Object.entries(SOURCE_META).map(([value, meta]) => ({
  value: value as DataSourceKind,
  label: meta.label,
}));

export const SPACE_ICON_OPTIONS = Object.keys(SPACE_ICON).map((value) => ({
  value: value as SpaceIconKey,
  label: value.replace(/^\w/, (ch) => ch.toUpperCase()),
}));
